import {
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectClasses,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LocationPinSvg } from 'assets/multi-port-icons';
import { brandColors } from 'brandColors';
import { useRegionContext } from 'common/contexts/RegionContext';
import * as React from 'react';
import { Region, RegionKey } from 'services/clients/regions';
import styles from '../../../../../styles/typography.module.css';
import { RegionalWrapper } from './RegionalWrapper';

interface Props {
  classes?: Partial<SelectClasses>;
  className?: string;
}

interface GeographicGroup {
  label: string;
  key: string;
  regions: Region[];
}

/**
 * Renders the dropdown for selecting the user's preferred region
 */
export const RegionDropdown: React.FC<Props> = (props) => {
  const { region: currentRegion, setRegion, availableRegions } = useRegionContext();

  /**
   * I tried breaking this into separate function components, but returning a
   * react fragment breaks the selection functionality!
   */
  const geographicRegions: GeographicGroup[] = React.useMemo(() => {
    const groups = availableRegions.reduce<GeographicGroup[]>((acc, region) => {
      let value = region.geographicRegion;
      if (!value) {
        value = 'Other';
      }

      const group = acc.find((g) => g.label === value);
      if (group) {
        group.regions.push(region);
      } else {
        acc.push({
          label: value,
          key: value,
          regions: [region],
        });
      }
      return acc;
    }, []);
    return groups;
  }, [availableRegions]);

  const items = React.useMemo(() => {
    return geographicRegions.flatMap((group, index) => {
      const regionItems = group.regions.map((region) => {
        const active = currentRegion.code === region.code;

        if (!region) {
          return null;
        }

        return (
          <MenuItem
            key={region.code}
            value={region.code}
            className="leading-none"
            style={{ fontSize: 'unset' }}
            classes={{ selected: 'text-blue-900 bg-white', root: 'z-[10000]' }}
          >
            <div className="flex items-center space-x-2">
              <div
                className={`flex items-center justify-center rounded-full ${
                  active ? 'bg-blue-300' : 'bg-gray-50'
                } w-6 h-6`}
              >
                <h5 className="font-bold">{region.initials}</h5>
              </div>
              <h4>{region.name}</h4>
            </div>
          </MenuItem>
        );
      });

      return [
        <ListSubheader key={group.key} className="px-4 py-4 m-0 leading-none">
          <h4 className="font-bold text-gray-900">{group.label}</h4>
        </ListSubheader>,
        ...regionItems,
        <div className="h-5" key={index} />,
      ];
    });
  }, [geographicRegions, currentRegion]);

  const handleChange = React.useCallback(
    (e: SelectChangeEvent<RegionKey>) => {
      const region = availableRegions.find((r) => r.code === e.target.value);
      if (!region) {
        return;
      }
      setRegion(region);
    },
    [availableRegions, setRegion]
  );

  const renderValue = React.useCallback(
    (key: RegionKey) => {
      const region = availableRegions.find((r) => r.code === key);

      return (
        <div className="flex items-center space-x-2">
          <LocationPinSvg fill={brandColors.colors.orange.DEFAULT} height="17px" />
          <h4 className="font-medium text-gray-800">{region?.name || ''}</h4>
        </div>
      );
    },
    [availableRegions]
  );

  return (
    <RegionalWrapper>
      <div className="">
        <FormControl classes={{ root: 'bg-blue-900' }}>
          <Select
            value={currentRegion.code}
            renderValue={renderValue}
            onChange={handleChange}
            size="small"
            className={`${props.className} border-gray-200`}
            classes={{
              ...props.classes,
            }}
            IconComponent={() => (
              <KeyboardArrowDownIcon
                style={{ fontSize: 17 }}
                className="ml-1 mr-2 pointer-events-none absolute right-0"
              />
            )}
            inputProps={{ className: 'py-2' }}
            MenuProps={{
              anchorOrigin: { horizontal: 'left', vertical: 'top' },
              transformOrigin: { horizontal: 'left', vertical: 'top' },
              classes: { list: `py-0 w-[320px] ${styles.font}` },
              PaperProps: {
                className: 'border border-gray-200 shadow-md',
                style: { marginTop: -1, marginLeft: -1 },
              },
            }}
          >
            <div className="flex items-center space-x-2 px-4 py-3">
              <LocationPinSvg fill={brandColors.colors.orange.DEFAULT} height="17px" />
              <h4 className="font-medium text-gray-900">Choose your port of operations</h4>
            </div>
            <Divider />
            {items}
          </Select>
        </FormControl>
      </div>
    </RegionalWrapper>
  );
};
RegionDropdown.displayName = 'DesktopNav.RegionDropdown';
